import 'twin.macro'

import { yupResolver } from '@hookform/resolvers/yup'
import { Ring } from '@uiball/loaders'
import ContainerWithSteps from 'components/UI/Container/ContainerWithSteps'
import BiometryProcessing from 'components/views/Biometry/BiometryProcessing'
import { BiometryMainBlock, BiometryModal, BiometryQRBlock } from 'components/views/Biometry/template'
import OtpVerification from 'components/views/OtpVerification'
import { useAppDispatch, useAppSelector } from 'hooks'
import { useForensics } from 'hooks/useForensics'
import { useGenerateQRLink } from 'hooks/useGenerateQRLink'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { Trans } from 'react-i18next'
import { useGetApplicationStatusQuery, useLazyGetApplicationStatusQuery } from 'redux/api'
import {
  useCCBorrowerSignMutation,
  useCCOwnBorrowerSignMutation,
  useCCPledgeBorrowerSignMutation,
  useStartSignBorrowerForensicsMutation,
  useVerifySignBorrowerForensicsMutation
} from 'redux/api/biometryApi'
import { useResendOtpMutation, useVerifyOtpMutation } from 'redux/api/leadsApi'
import { getCurrentFlowId } from 'redux/store/slices/userSlice'
import { getNotificationsSelector, removeStepNotificationData } from 'redux/store/slices/wsSlice'
import { ApplicationEvents } from 'share/enums/events'
import { FLOW_STEPS } from 'share/types'
import { getWsStepStatus } from 'utils'
import Emitter from 'utils/emitter'
import * as yup from 'yup'

const otpSchema = yup.object({
  code: yup.string().min(6, 'create-lead-schema.otp-min').required('create-lead-schema.required')
})

type OtpSchemaValues = yup.InferType<typeof otpSchema>

const CertificationCenterPage = () => {
  // Tools
  const dispatch = useAppDispatch()
  const qrValue = useGenerateQRLink()

  // Selectors
  const currentFlowId = useAppSelector(getCurrentFlowId)
  const { SIGN_BIOMETRY_FORENSICS } = useAppSelector(getNotificationsSelector)

  // State
  const [isOpen, setIsOpen] = useState(false)
  const [isBiometryProcessing, setIsBiometryProcessing] = useState(false)

  // Queries
  const {
    data,
    isLoading: isStatusQueryLoading,
    refetch
  } = useGetApplicationStatusQuery(currentFlowId as string, {
    skip: !currentFlowId
  })

  // Mutations
  const [fetchStatus, { isLoading: isStatusLoading }] = useLazyGetApplicationStatusQuery()
  const [startForensics, { isLoading: isStartForensicsLoading }] = useStartSignBorrowerForensicsMutation()
  const [verifyForensics, { isLoading: isForensicsVerifyLoading }] = useVerifySignBorrowerForensicsMutation()
  const [resendOtp] = useResendOtpMutation()
  const [verifyOtp, { isLoading: isOtpVerifying }] = useVerifyOtpMutation()
  const [startCCBorrowerSign, { isLoading: isStartCCBorrowerSignLoading }] = useCCBorrowerSignMutation()
  const [startCCOwnBorrowerSign, { isLoading: isStartCCOwnBorrowerSignLoading }] = useCCOwnBorrowerSignMutation()
  const [startCCPledgeBorrowerSign, { isLoading: isStartCCPledgeBorrowerSignLoading }] =
    useCCPledgeBorrowerSignMutation()

  // Form
  const { control: otpControl } = useForm<OtpSchemaValues>({
    defaultValues: {
      code: ''
    },
    mode: 'onChange',
    resolver: yupResolver(otpSchema)
  })

  // Computed values
  const isLoading =
    isStatusLoading ||
    isStartForensicsLoading ||
    isForensicsVerifyLoading ||
    isStartCCBorrowerSignLoading ||
    isStartCCPledgeBorrowerSignLoading ||
    isStartCCOwnBorrowerSignLoading

  const isSmsSent = useMemo(() => {
    if (!data) return false
    const { current_step, next_step } = data

    if (next_step && next_step !== FLOW_STEPS.SEND_OTP_SMS) return false
    if (!current_step) return false
    if (current_step.step === FLOW_STEPS.SEND_OTP_SMS) return true

    return false
  }, [data])

  // Handlers
  const handleStartForensics = async () => {
    try {
      if (!currentFlowId) throw new Error('Flow ID is not defined')

      dispatch(removeStepNotificationData(FLOW_STEPS.SIGN_BIOMETRY_FORENSICS))
      const response = await fetchStatus(currentFlowId).unwrap()
      const { step_id } = await startForensics(response.id).unwrap()
      runOzLiveness(step_id, FLOW_STEPS.BIOMETRY_FORENSICS)
    } catch (error) {
      console.log(error)
    }
  }

  const onForensicsComplete = async () => {
    try {
      if (!currentFlowId) throw new Error('Flow ID is not defined')
      const OzLiveness = window.OzLiveness
      const { current_step } = await fetchStatus(currentFlowId).unwrap()
      if (!current_step) throw new Error('Current step is not defined')
      const { id } = current_step
      await verifyForensics(id as string).unwrap()
      OzLiveness.close()
    } catch (error) {
      console.log(error)
    }
  }

  const runOzLiveness = useForensics({
    onSubmit: () => {
      setIsBiometryProcessing(true)
    },
    onComplete: onForensicsComplete
  })

  const handleOtpVerify = async (value: string | undefined) => {
    try {
      if (!currentFlowId || !value) throw new Error('Flow ID or OTP is not defined')
      const { current_step } = await fetchStatus(currentFlowId).unwrap()
      if (!current_step) throw new Error('Current step is not defined')
      if (current_step.step !== FLOW_STEPS.SEND_OTP_SMS) throw new Error('Invalid step')
      await verifyOtp({
        step_uuid: current_step.id,
        passcode: value
      }).unwrap()
      // Decide which mutation to call based on the flow type
      let mutation = startCCBorrowerSign
      if (current_step.flow_type === 'COLLATERAL') {
        mutation = startCCPledgeBorrowerSign
      }
      if (current_step.flow_type === 'OWN' || current_step.flow_type === 'OWN_AUTO') {
        mutation = startCCOwnBorrowerSign
      }
      await mutation(currentFlowId).unwrap()
      Emitter.emit(ApplicationEvents.NAVIGATE_TO_NEXT_PAGE)
    } catch (err) {
      console.log(err)
    }
  }

  const handleOtpResend = useCallback(async () => {
    try {
      if (!currentFlowId) throw new Error('Flow ID is not defined')
      const { current_step } = await fetchStatus(currentFlowId).unwrap()
      if (!current_step) throw new Error('Current step is not defined')
      if (current_step.step !== FLOW_STEPS.SEND_OTP_SMS) throw new Error('Invalid step')
      await resendOtp(current_step.id)
    } catch (error) {
      console.log(error)
    }
  }, [currentFlowId, fetchStatus, resendOtp])

  // Effects
  useEffect(() => {
    if (getWsStepStatus(SIGN_BIOMETRY_FORENSICS) === 'DONE') {
      setIsBiometryProcessing(false)
      refetch()
      dispatch(removeStepNotificationData(FLOW_STEPS.SIGN_BIOMETRY_FORENSICS))
    }
  }, [SIGN_BIOMETRY_FORENSICS, dispatch, refetch])

  if (isStatusQueryLoading) {
    return (
      <ContainerWithSteps>
        <div tw='flex justify-center items-center'>
          <Ring color='var(--primary-color)' size={64} />
        </div>
      </ContainerWithSteps>
    )
  }

  if (isSmsSent) {
    return (
      <ContainerWithSteps>
        <div>
          <OtpVerification
            variant='certification-otp'
            name='code'
            showPhoneChange={false}
            otpLength={6}
            control={otpControl}
            disabled={isOtpVerifying}
            onOtpVerify={handleOtpVerify}
            onOtpTimerReset={handleOtpResend}
          />
        </div>
      </ContainerWithSteps>
    )
  }

  return (
    <ContainerWithSteps>
      {isBiometryProcessing ? (
        <BiometryProcessing />
      ) : (
        <div>
          <BiometryMainBlock
            isLoading={isLoading}
            title={<Trans i18nKey='3rd-party-pledger.signing-title' />}
            onButtonClick={() => setIsOpen(true)}
          />
          <BiometryQRBlock QRValue={qrValue} />
          <BiometryModal isOpen={isOpen} onOpenChange={setIsOpen} onContinueButtonClick={handleStartForensics} />
        </div>
      )}
    </ContainerWithSteps>
  )
}

export default CertificationCenterPage
